@if (!isLoading()) {
  <section class="content-section page-intro">
    <p>{{ 'organizationAdmin.groups.description' | transloco }}</p>
    @if (canEdit()) {
      <button mat-flat-button color="primary" type="button" [disabled]="isSaving()" (click)="openCreateGroupDialog()">
        <mat-icon>group_add</mat-icon>
        {{ 'organizationAdmin.groups.createGroup' | transloco }}
      </button>
    }
  </section>

  <div class="groups-layout">
    <div class="group-list">
      @for (group of groups(); track group.id) {
        <button type="button" class="group-list-item" [class.selected]="group.id === selectedGroupId()" (click)="selectGroup(group)">
          <span class="group-list-item-text">
            <span class="group-name">{{ group.name }}</span>
            <span class="group-member-count">{{ 'organizationAdmin.groups.memberCount' | transloco: { count: group.memberCount } }}</span>
          </span>
          <span class="permission-pill">
            <mat-icon>{{ group.isDirectoryPermissionsEnabled ? 'lock' : 'lock_open' }}</mat-icon>
            {{ (group.isDirectoryPermissionsEnabled ? 'organizationAdmin.groups.permissionsRestricted' : 'organizationAdmin.groups.permissionsAllFolders') | transloco }}
          </span>
        </button>
      } @empty {
        <p class="empty-text">{{ 'organizationAdmin.groups.noGroups' | transloco }}</p>
      }
    </div>

    @if (selectedGroup(); as group) {
      <div class="group-detail">
        <section class="content-section detail-card">
          <div class="detail-card-header">
            <h3>{{ group.name }}</h3>
            <p class="detail-meta">{{ 'organizationAdmin.groups.memberCount' | transloco: { count: group.memberCount } }}</p>
          </div>

          <div class="member-pills">
            @for (member of selectedMembers(); track member.id) {
              <span class="member-pill">
                <mat-icon>person</mat-icon>
                {{ member.name }}
              </span>
            } @empty {
              <span class="empty-text">{{ 'organizationAdmin.groups.noMembers' | transloco }}</span>
            }
          </div>

          @if (canEdit()) {
            <div class="detail-actions">
              <button mat-stroked-button color="primary" type="button" (click)="openEditMembersDialog(group)" [disabled]="isSaving() || users().length === 0">
                <mat-icon>group</mat-icon>
                {{ 'organizationAdmin.groups.editMembers' | transloco }}
              </button>

              <button mat-button color="primary" class="link-button" type="button" [disabled]="isSaving()" (click)="openRenameGroupDialog(group)">
                <mat-icon>edit</mat-icon>
                {{ 'organizationAdmin.groups.rename' | transloco }}
              </button>
              <button mat-button color="primary" class="link-button" type="button" [disabled]="isSaving()" (click)="deleteGroup(group)">
                <mat-icon>delete</mat-icon>
                {{ 'organizationAdmin.groups.deleteGroup' | transloco }}
              </button>
            </div>
          }
        </section>

        <mat-expansion-panel class="permissions-panel" [expanded]="true">
          <mat-expansion-panel-header collapsedHeight="70px" expandedHeight="70px">
            <mat-panel-title class="grid-item-title">{{ 'organizationAdmin.groups.folderPermissions.title' | transloco }}</mat-panel-title>
          </mat-expansion-panel-header>

          <div class="permissions-header">
            <p>{{ 'organizationAdmin.groups.folderPermissions.inheritHint' | transloco }}</p>
            <div class="permissions-error" [style.visibility]="hasNoFolderAccess() ? 'visible' : 'hidden'">
              <mat-icon>warning</mat-icon>
              <span>{{ 'organizationAdmin.groups.folderPermissions.noFolderAccess' | transloco }}</span>
            </div>
          </div>

          <div class="folder-toolbar">
            <mat-form-field appearance="outline" class="folder-search" subscriptSizing="dynamic">
              <mat-icon matPrefix>search</mat-icon>
              <input
                matInput
                [formControl]="folderSearchControl"
                [placeholder]="'common.buttons.search' | transloco"
                [attr.aria-label]="'common.buttons.search' | transloco" />
            </mat-form-field>
            <app-segmented-toggle
              class="permissions-toggle"
              [options]="permissionsToggleOptions()"
              [value]="permissionsDraft().isRestricted"
              [disabled]="!canEdit() || isSaving()"
              (valueChange)="setPermissionsEnabled($event)"></app-segmented-toggle>
          </div>

          <div class="folder-tree" [class.disabled]="!permissionsDraft().isRestricted">
            @if (canEdit()) {
              <div class="folder-tree-row set-all-row">
                <span class="set-all-label">{{ 'organizationAdmin.groups.folderPermissions.setAll' | transloco }}</span>
                <app-segmented-toggle
                  class="level-toggle"
                  [options]="levelToggleOptions()"
                  [value]="allFoldersLevel()"
                  [disabled]="isSaving() || !permissionsDraft().isRestricted"
                  (valueChange)="setAllFolderLevels($event)"></app-segmented-toggle>
              </div>
            }
            @for (row of filteredFolderRows(); track row.id) {
              <div class="folder-tree-row" [style.padding-left]="row.paddingLeft">
                @if (row.canCollapse) {
                  <button mat-icon-button type="button" class="folder-expand-button" (click)="toggleFolderExpanded(row.id)">
                    <mat-icon>{{ row.isExpanded ? 'expand_more' : 'chevron_right' }}</mat-icon>
                  </button>
                } @else {
                  <span class="folder-expand-button"></span>
                }
                <mat-icon class="folder-icon" [class.inactive]="row.hasNoAccess">{{ row.isTopFolder ? 'home' : 'folder' }}</mat-icon>
                <span class="folder-name" [class.inactive]="row.hasNoAccess">
                  {{ row.name }}
                  @if (row.isUncovered) {
                    <mat-icon class="uncovered-icon" [matTooltip]="'organizationAdmin.groups.folderPermissions.uncovered' | transloco">warning</mat-icon>
                  }
                </span>
                <app-segmented-toggle
                  class="level-toggle"
                  [options]="row.hasSharedSubfolder ? parentLevelToggleOptions() : levelToggleOptions()"
                  [value]="row.level"
                  [disabled]="!canEdit() || isSaving()"
                  (valueChange)="setFolderLevel(row.id, $event)"></app-segmented-toggle>
              </div>
            } @empty {
              <p class="empty-text tree-empty-text">{{ 'common.text.noResults' | transloco }}</p>
            }
          </div>

          @if (canEdit()) {
            <mat-action-row class="permissions-actions">
              <button mat-button color="primary" type="button" [disabled]="!hasPermissionChanges() || isSaving()" (click)="resetPermissionsForm()">
                {{ 'common.buttons.discard' | transloco }}
              </button>
              <button mat-flat-button color="primary" type="button" [disabled]="!hasPermissionChanges() || isSaving()" (click)="savePermissions(group)">
                <mat-icon>check</mat-icon>
                {{ 'common.buttons.save' | transloco }}
              </button>
            </mat-action-row>
          }
        </mat-expansion-panel>

      @if (isOrganizationVisibilityEnabled) {
      <mat-expansion-panel class="permissions-panel" [expanded]="true">
          <mat-expansion-panel-header collapsedHeight="70px" expandedHeight="70px">
            <mat-panel-title class="grid-item-title">{{ 'organizationAdmin.groups.visibleOrganizations.title' | transloco }}</mat-panel-title>
          </mat-expansion-panel-header>

          <div class="permissions-header">
            <p>{{ 'organizationAdmin.groups.visibleOrganizations.description' | transloco }}</p>
            <app-segmented-toggle
              class="permissions-toggle"
              [options]="visibleOrganizationsToggleOptions()"
              [value]="visibilityDraft().isRestricted"
              [disabled]="!canEdit() || isSaving()"
              (valueChange)="setVisibleOrganizationsEnabled($event)"></app-segmented-toggle>
          </div>

          <div class="folder-tree" [class.disabled]="!visibilityDraft().isRestricted">
            @for (row of visibleOrganizationRows(); track row.organization.id) {
              <div class="folder-tree-row">
                <mat-checkbox
                  color="primary"
                  [checked]="row.isVisible"
                  [disabled]="!canEdit() || isSaving()"
                  (change)="setOrganizationVisible(row.organization.id, $event.checked)">
                  {{ row.organization.name }}
                </mat-checkbox>
                @if (row.isUncovered) {
                  <mat-icon class="uncovered-icon" [matTooltip]="'organizationAdmin.groups.visibleOrganizations.uncovered' | transloco">warning</mat-icon>
                }
              </div>
            } @empty {
              <p class="empty-text tree-empty-text">{{ 'organizationAdmin.friendRequests.emptyConnected' | transloco }}</p>
            }
          </div>

          @if (canEdit()) {
            <mat-action-row class="permissions-actions">
              <button mat-button color="primary" type="button" [disabled]="!hasVisibilityChanges() || isSaving()" (click)="resetVisibilityForm()">
                {{ 'common.buttons.discard' | transloco }}
              </button>
              <button mat-flat-button color="primary" type="button" [disabled]="!hasVisibilityChanges() || isSaving()" (click)="saveVisibility(group)">
                <mat-icon>check</mat-icon>
                {{ 'common.buttons.save' | transloco }}
              </button>
            </mat-action-row>
          }
        </mat-expansion-panel>
      }
      </div>
    }
  </div>
}
